import { Box, Button } from "@mui/material";
import { isUnlimitedLimit } from "helpers/methods";
import {
  manageSeatsButtonSx,
  seatsCardSx,
  seatsFillSx,
  seatsHeadlineSx,
  seatsLabelSx,
  seatsMeterColumnSx,
  seatsNoteSx,
  seatsPerSeatNoteSx,
  seatsTrackSx,
  seatsUsageSx,
} from "screens/Settings/Team/Team.styles";

interface SeatsCardProps {
  /** Seats in use: Active plus invited-but-not-yet-accepted users. */
  used: number;
  /** Seats the account has bought — the number it is billed for. */
  total: number;
  /**
   * Per-seat price and the next renewal total. Omitted on plans that are not
   * billed per seat, where the price is flat.
   */
  perSeatNote?: string;
  /**
   * Whether the seat count can be changed here. Only per-seat plans buy seats;
   * for the rest the action leads to the plan screen.
   */
  canManageSeats?: boolean;
  onManageSeats: () => void;
}

/**
 * Seat meter card sitting between the page header and the member table, drawn
 * exactly as the Teams design specifies. Geometry, colour and type come from
 * `Team.tokens.ts`.
 */
const SeatsCard = ({
  used,
  total,
  perSeatNote,
  canManageSeats = false,
  onManageSeats,
}: SeatsCardProps) => {
  // An uncapped plan stores a sentinel limit, so there is no meaningful "of N"
  // to show and the meter would sit at 0% forever.
  const isUnlimited = isUnlimitedLimit(total);
  const isFull = !isUnlimited && used >= total;
  const available = Math.max(0, total - used);
  const percent =
    !isUnlimited && total > 0
      ? Math.min(100, Math.round((used / total) * 100))
      : 0;

  // Seats are bought up front, so a full account buys more (or upgrades when
  // the plan's cap is reached) rather than being billed automatically.
  const note = isUnlimited
    ? "No seat limit on this plan"
    : isFull
      ? canManageSeats
        ? "All seats in use — buy more seats to invite more people"
        : "All seats in use — upgrade your plan to invite more people"
      : `${available} seat${available === 1 ? "" : "s"} available`;

  return (
    <Box sx={seatsCardSx}>
      <Box sx={seatsMeterColumnSx}>
        <Box sx={seatsHeadlineSx}>
          <Box component="span" sx={seatsLabelSx}>
            Seats
          </Box>
          <Box component="span" sx={seatsUsageSx}>
            {isUnlimited
              ? `${used} ${used === 1 ? "seat" : "seats"} in use`
              : `${used} of ${total} purchased seats in use`}
          </Box>
          <Box component="span" sx={seatsNoteSx}>
            {note}
          </Box>
        </Box>
        {!isUnlimited && (
          <Box sx={seatsTrackSx}>
            <Box sx={seatsFillSx(percent, isFull)} />
          </Box>
        )}
        {perSeatNote && (
          <Box component="span" sx={seatsPerSeatNoteSx}>
            {perSeatNote}
          </Box>
        )}
      </Box>
      <Button
        id="manage_seats_button"
        variant="text"
        disableRipple
        onClick={onManageSeats}
        sx={manageSeatsButtonSx}
      >
        {canManageSeats ? "Manage seats" : isFull ? "Upgrade plan" : "Manage plan"}
      </Button>
    </Box>
  );
};

export default SeatsCard;
